import { setFirebaseMessaging, setFirebaseMessagingToken } from 'core/store/app/actions'
import { ApplicationState } from 'core/store/reducers'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getMessaging, getToken, MessagePayload, onMessage } from 'firebase/messaging'
import { useNotificationPermission } from 'core/services/notifications/useNotificationPermission'
import { SnackbarKey, useSnackbar } from 'notistack'
import { Button, createStyles, makeStyles, Theme } from '@material-ui/core'
import { Visibility } from '@material-ui/icons'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        snackbarBody: {
            maxWidth: '300px',
        },
        snackbarTitle: {
            fontSize: '1rem',
        },
        snackbarContent: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxHeight: '3.2rem',
            lineHeight: '1.6rem',
        },
    }),
)

const useFirebaseMessaging = () => {
    const { REACT_APP_FCM_KEY } = process.env
    if (!REACT_APP_FCM_KEY) {
        throw Error('undefined REACT_APP_FCM_KEY')
    }
    const dispatch = useDispatch()
    const classes = useStyles()
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const { t } = useTranslation()
    const { checkNotificationPermission } = useNotificationPermission()
    const { firebaseApp, firebaseMessaging, firebaseMessagingToken } = useSelector(
        ({ app }: ApplicationState) => ({
            firebaseApp: app.firebaseApp,
            firebaseMessaging: app.firebaseMessaging,
            firebaseMessagingToken: app.firebaseMessagingToken,
        }),
    )
    const [hasRun, setHasRun] = useState(firebaseMessaging !== undefined)
    const [hasRequestToken, setHasRequestToken] = useState(firebaseMessaging !== undefined)
    const [hasMessageCatcher, setHasMessageCatcher] = useState(false)

    useEffect(() => {
        if (!hasRun && firebaseApp && !firebaseMessaging) {
            setHasRun(true)
            try {
                const messaging = getMessaging(firebaseApp)
                dispatch(setFirebaseMessaging(messaging))
            } catch (error) {
                console.error("Erreur lors de l'initialisation Firebase Messaging:", error)
            }
        }
    }, [firebaseApp, firebaseMessaging, dispatch, hasRun])

    useEffect(() => {
        if (!hasRequestToken && firebaseMessaging && !firebaseMessagingToken) {
            setHasRequestToken(true)
            if (checkNotificationPermission() === 'granted') {
                getToken(firebaseMessaging, {
                    vapidKey: REACT_APP_FCM_KEY,
                }).then((messagingToken) => {
                    if (messagingToken) {
                        dispatch(setFirebaseMessagingToken(messagingToken))
                    }
                })
            }
        }
    }, [
        REACT_APP_FCM_KEY,
        checkNotificationPermission,
        dispatch,
        firebaseMessaging,
        firebaseMessagingToken,
        hasRequestToken,
    ])

    useEffect(() => {
        if (!hasMessageCatcher && firebaseMessaging) {
            setHasMessageCatcher(true)
            onMessage(firebaseMessaging, (payload: MessagePayload) => {
                const action = (key: SnackbarKey) => {
                    return (
                        <>
                            {payload.data?.url && (
                                <Button
                                    variant={'outlined'}
                                    color={'inherit'}
                                    startIcon={<Visibility />}
                                    href={payload.data?.url}
                                >
                                    {t('actions.check')}
                                </Button>
                            )}
                            <IconButton color={'inherit'} onClick={() => closeSnackbar(key)}>
                                <CloseIcon />
                            </IconButton>
                        </>
                    )
                }
                enqueueSnackbar(
                    <div className={classes.snackbarBody}>
                        <div className={classes.snackbarTitle}>{payload.notification?.title}</div>
                        <div className={classes.snackbarContent}>{payload.notification?.body}</div>
                    </div>,
                    {
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                        variant: 'default',
                        persist: true,
                        action,
                    },
                )
            })
        }
    }, [
        classes.snackbarBody,
        classes.snackbarContent,
        classes.snackbarTitle,
        closeSnackbar,
        enqueueSnackbar,
        firebaseMessaging,
        hasMessageCatcher,
        t,
    ])

    return firebaseMessaging
}

export default useFirebaseMessaging
